import React from 'react';
import {
  Users,
  UsersRound,
  Settings,
  LogOut,
  Home,
  Building2,
  GraduationCap,
  Menu,
  X
} from 'lucide-react';
import { Logo } from './Logo';
import { useSidebar } from '../../contexts/SidebarContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuth } from '../../contexts/AuthContext';

interface MenuItem {
  id: string;
  icon: React.FC<{ className?: string }>;
  label: string;
}

interface SidebarProps {
  currentPage: string;
  onPageChange: (page: string) => void;
  onLogout: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  currentPage,
  onPageChange,
  onLogout
}) => {
  const { isMobileOpen, setMobileOpen } = useSidebar();
  const { isSuperAdmin, isTrainer } = usePermissions();
  const { organizationName } = useAuth();

  const superAdminMenuItems: MenuItem[] = [
    { id: 'dashboard',     icon: Home,         label: 'Tableau de bord' },
    { id: 'organizations', icon: Building2,     label: 'Organisations' },
    { id: 'learners',      icon: Users,         label: 'Apprenants' },
    { id: 'groups',        icon: UsersRound,    label: 'Groupes' },
    { id: 'programs',      icon: GraduationCap, label: 'Formations' },
    { id: 'settings',      icon: Settings,      label: 'Paramètres' },
  ];

  const clientMenuItems: MenuItem[] = [
    { id: 'dashboard', icon: Home,         label: 'Tableau de bord' },
    { id: 'learners',  icon: Users,         label: 'Mes apprenants' },
    { id: 'groups',    icon: UsersRound,    label: 'Groupes' },
    { id: 'programs',  icon: GraduationCap, label: 'Formations' },
  ];

  const menuItems = isSuperAdmin ? superAdminMenuItems : clientMenuItems;

  const handlePageChange = (page: string) => {
    onPageChange(page);
    setMobileOpen(false);
  };

  const sidebarContent = (
    <div className="h-full flex flex-col p-4">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <Logo />
          <button
            onClick={() => setMobileOpen(false)}
            className="lg:hidden text-ink-soft hover:text-ink p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {!isSuperAdmin && organizationName && (
          <div className="mt-3 bg-cream-high rounded-lg px-3 py-2">
            <p className="text-sm text-ink font-semibold truncate">{organizationName}</p>
          </div>
        )}
      </div>

      <div className="flex-1 space-y-1">
        {menuItems.map(({ id, icon: Icon, label }) => (
          <button
            key={id}
            onClick={() => handlePageChange(id)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all duration-200 ${
              currentPage === id
                ? 'bg-coral text-white shadow-cta'
                : 'text-ink-soft hover:bg-cream-high hover:text-ink'
            }`}
          >
            <Icon className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm font-medium">{label}</span>
          </button>
        ))}
      </div>

      <div className="mb-4 px-3">
        <div className="text-xs text-ink-soft">
          {isSuperAdmin ? 'Super Admin' : isTrainer ? 'Formateur' : 'Client'}
        </div>
      </div>

      <div className="space-y-1 border-t border-cream-high pt-4">
        <button
          onClick={onLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all duration-200 text-ink-soft hover:bg-cream-high hover:text-ink"
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm font-medium">Déconnexion</span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile burger button */}
      <button
        onClick={() => setMobileOpen(true)}
        className="fixed top-4 left-4 z-40 lg:hidden bg-cream border border-cream-high text-ink p-2 rounded-xl shadow-ambient"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar — fixed desktop, slide-in mobile */}
      <aside
        className={`fixed left-0 top-0 h-full w-64 bg-cream/80 backdrop-blur-md z-50 transition-transform duration-300 lg:translate-x-0 ${
          isMobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {sidebarContent}
      </aside>
    </>
  );
};
